import {
  Cloud,
  CreditCard,
  HandCoins,
  LifeBuoy,
  Link as LinkIcon,
  type LucideIcon,
  Megaphone,
  PanelTop,
  Plug,
  Sparkles,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Panel } from '@/components/redesign/bits'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { PageTitle } from '@/app/layout'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { AppSettings } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatRelative } from '@/lib/format'
import { useDiscounts } from '@/features/discounts/api'
import {
  type SettingsResponse,
  useClearCloudflareCache,
  useCloudflareCache,
  useSaveSettings,
  useSettings,
} from './api'

type Section =
  | 'ranking_updates'
  | 'announcement'
  | 'banner'
  | 'cache'
  | 'support'
  | 'plans'
  | 'referral'
  | 'links'
  | 'wordpress'

const SECTIONS: {
  key: Section
  label: string
  icon: LucideIcon
  /** "On", "Off" and the like beside the name, from what's saved. */
  state?: (v: AppSettings) => string
}[] = [
  { key: 'ranking_updates', label: 'Google ranking updates', icon: Sparkles },
  {
    key: 'announcement',
    label: 'In-app announcement',
    icon: Megaphone,
    state: (v) => (v.announcement.enabled ? 'On' : 'Off'),
  },
  {
    key: 'banner',
    label: 'Website banner',
    icon: PanelTop,
    state: (v) => (v.site_announcement.enabled ? 'On' : 'Off'),
  },
  { key: 'cache', label: 'Cloudflare cache', icon: Cloud },
  { key: 'support', label: 'Support promise', icon: LifeBuoy },
  {
    key: 'plans',
    label: 'Paid plans',
    icon: CreditCard,
    state: (v) => (v.billing.upgrades_open ? 'Open' : 'Paused'),
  },
  {
    key: 'referral',
    label: 'Referral program',
    icon: HandCoins,
    state: (v) => (v.referrals.enabled ? 'On' : 'Off'),
  },
  { key: 'links', label: 'Links', icon: LinkIcon },
  {
    key: 'wordpress',
    label: 'WordPress connector',
    icon: Plug,
    state: (v) => (v.wordpress.connector_enabled ? 'On' : 'Off'),
  },
]

/**
 * What every customer sees, editable without a deploy (redesign of 2026-10-16): a side menu of
 * sections, each a form with a preview where customers see the result. Owners and admins edit;
 * everyone else sees them. Each save is recorded in the audit log.
 */
export function SettingsPage() {
  const settings = useSettings()
  const [params, setParams] = useSearchParams()
  const asked = params.get('section')
  const section: Section = SECTIONS.some((s) => s.key === asked) ? (asked as Section) : 'announcement'

  return (
    <>
      <PageTitle
        title="Announcements and settings"
        description="What every customer sees: the announcement bar, the website banner, support hours and whether paid plans can be bought. The app picks up a change within a few minutes, without a deploy. Nothing secret belongs here."
      />
      {settings.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : settings.isError ? (
        <div className="card">
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        </div>
      ) : (
        <div className="sidemenu">
          <nav className="smenu" aria-label="Settings sections">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={cn('sg', section === s.key && 'on')}
                aria-current={section === s.key ? 'page' : undefined}
                onClick={() => setParams({ section: s.key }, { replace: true })}
              >
                <s.icon aria-hidden="true" />
                {s.label}
                {s.state && <span className="c">{s.state(settings.data.values)}</span>}
              </button>
            ))}
          </nav>
          <div className="sec" style={{ gap: 16 }}>
            {section === 'announcement' && <AnnouncementForm data={settings.data} />}
            {section === 'banner' && <SiteAnnouncementForm data={settings.data} />}
            {section === 'cache' && <CloudflareCacheForm />}
            {section === 'support' && <SupportForm data={settings.data} />}
            {section === 'plans' && <PaidPlansForm data={settings.data} />}
            {section === 'referral' && <ReferralsForm data={settings.data} />}
            {section === 'links' && <LinksForm data={settings.data} />}
            {section === 'ranking_updates' && <RankingUpdatesForm data={settings.data} />}
            {section === 'wordpress' && <WordPressForm data={settings.data} />}
          </div>
        </div>
      )}
    </>
  )
}

/** Who last changed it and when, and Save for those who can. */
function Foot({ data, k, saving }: { data: SettingsResponse; k: keyof AppSettings; saving: boolean }) {
  const h = data.history[k] ?? { updated_at: null, updated_by: null }
  return (
    <div className="bar">
      <span style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
        {h.updated_at
          ? `Last changed ${formatRelative(h.updated_at)}${h.updated_by ? ` by ${h.updated_by}` : ''}`
          : 'Never changed: the defaults'}
      </span>
      {data.can_edit && (
        <button type="submit" className="btn primary push" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  )
}

/** A setting that's on or off, as a row with a switch. */
function SwitchRow({
  label,
  sub,
  checked,
  disabled,
  onChange,
}: {
  label: string
  sub?: ReactNode
  checked: boolean
  disabled: boolean
  onChange: (on: boolean) => void
}) {
  return (
    <div className="srow" style={{ paddingTop: 0 }}>
      <div>
        <b>{label}</b>
        {sub && <small>{sub}</small>}
      </div>
      <button
        type="button"
        role="switch"
        className="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
    </div>
  )
}

function useForm<K extends keyof AppSettings>(
  key: K,
  data: SettingsResponse,
  defaults: AppSettings[K] = data.values[key],
) {
  const save = useSaveSettings(key)
  const [value, setValue] = useState<AppSettings[K]>(data.values[key] ?? defaults)
  const errors = isApiError(save.error) ? save.error.errors : {}
  const submit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    save.mutate(value, {
      onSuccess: () => toast.success('Saved. The app shows it within a few minutes.'),
      onError: (err) => !(isApiError(err) && err.isValidation) && toast.error(errorMessage(err)),
    })
  }
  return {
    value,
    set: (patch: Partial<AppSettings[K]>) => setValue({ ...value, ...patch }),
    errors,
    submit,
    saving: save.isPending,
  }
}

function AnnouncementForm({ data }: { data: SettingsResponse }) {
  const f = useForm('announcement', data)
  const off = !data.can_edit

  return (
    <Panel
      title="In-app announcement"
      sub="A bar across the top of app.arvaseo.com, for planned maintenance, a delay, or news. People can dismiss it; a new message shows again."
    >
      <form onSubmit={f.submit} className="form">
        <SwitchRow
          label="Show the announcement"
          sub="Turn off to hide it without losing the text"
          checked={f.value.enabled}
          disabled={off}
          onChange={(on) => f.set({ enabled: on })}
        />
        <Field
          label="Message"
          htmlFor="a-message"
          required={f.value.enabled}
          error={f.errors.message?.[0]}
          hint="Up to 280 characters."
        >
          <Textarea
            id="a-message"
            rows={2}
            maxLength={280}
            disabled={off}
            value={f.value.message}
            onChange={(e) => f.set({ message: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Style" htmlFor="a-tone" error={f.errors.tone?.[0]}>
            <NativeSelect
              id="a-tone"
              disabled={off}
              value={f.value.tone}
              onChange={(e) => f.set({ tone: e.target.value as AppSettings['announcement']['tone'] })}
            >
              <option value="info">Information</option>
              <option value="warning">Warning</option>
              <option value="success">Good news</option>
            </NativeSelect>
          </Field>
          <Field label="Link text (optional)" htmlFor="a-label" error={f.errors.link_label?.[0]}>
            <Input
              id="a-label"
              disabled={off}
              value={f.value.link_label}
              onChange={(e) => f.set({ link_label: e.target.value })}
              placeholder="Read more"
            />
          </Field>
          <Field label="Link address (optional)" htmlFor="a-url" error={f.errors.link_url?.[0]}>
            <Input
              id="a-url"
              type="url"
              disabled={off}
              value={f.value.link_url}
              onChange={(e) => f.set({ link_url: e.target.value })}
              placeholder="https://"
            />
          </Field>
        </div>
        <div className="fld">
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>Preview</span>
          <div className="preview">
            <AnnouncementBar
              tone={f.value.tone}
              message={f.value.message}
              link={f.value.link_url ? f.value.link_label || 'Read more' : ''}
            />
          </div>
        </div>
        <Foot data={data} k="announcement" saving={f.saving} />
      </form>
    </Panel>
  )
}

/** The bar as customers see it, from what's typed so far. */
function AnnouncementBar({
  tone,
  message,
  code,
  link,
}: {
  tone: AppSettings['announcement']['tone']
  message: string
  code?: string
  link: string
}) {
  return (
    <div
      className="ann"
      style={tone === 'warning' ? { background: 'var(--warn-soft)', color: 'var(--ink)' } : undefined}
    >
      <Sparkles aria-hidden="true" className="size-4 flex-none" />
      <span>{message.trim() || <span style={{ opacity: 0.6 }}>Your message shows here</span>}</span>
      {code && <span className="chip lime">{code}</span>}
      {link && <a>{link}</a>}
    </div>
  )
}

/** "2026-10-31T23:59" in the browser's time zone ↔ ISO 8601. */
const toLocalInput = (iso: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

type BannerValue = AppSettings['site_announcement'] & { starts_at?: string }

/** The fields both banners share: message, code, button, dates and pages, and a preview. */
function BannerFields({
  id,
  v,
  set,
  errors,
  off,
}: {
  id: string
  v: BannerValue
  set: (patch: Partial<BannerValue>) => void
  errors: Record<string, string[] | undefined>
  off: boolean
}) {
  const scheduled = v.starts_at !== undefined
  return (
    <>
      <Field
        label="Website message"
        htmlFor={`${id}-message`}
        required={v.enabled}
        error={errors.message?.[0]}
        hint="Up to 160 characters, e.g. “Launch week: 20% off your first payment on any paid plan.”"
      >
        <Input
          id={`${id}-message`}
          maxLength={160}
          disabled={off}
          value={v.message}
          onChange={(e) => set({ message: e.target.value })}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Promo code (optional)"
          htmlFor={`${id}-code`}
          error={errors.code?.[0]}
          hint="Letters, numbers, - and _. Shown with a copy button."
        >
          <Input
            id={`${id}-code`}
            maxLength={40}
            disabled={off}
            value={v.code}
            onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/\s+/g, '') })}
            placeholder="LAUNCH20"
          />
        </Field>
        <Field label="Button text (optional)" htmlFor={`${id}-label`} error={errors.link_label?.[0]}>
          <Input
            id={`${id}-label`}
            maxLength={40}
            disabled={off}
            value={v.link_label}
            onChange={(e) => set({ link_label: e.target.value })}
            placeholder="See pricing"
          />
        </Field>
        <Field label="Button link (optional)" htmlFor={`${id}-url`} error={errors.link_url?.[0]}>
          <Input
            id={`${id}-url`}
            type="url"
            disabled={off}
            value={v.link_url}
            onChange={(e) => set({ link_url: e.target.value })}
            placeholder="https://arvaseo.com/pricing/"
          />
        </Field>
      </div>
      <div className={cn('grid gap-4', scheduled ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        {scheduled && (
          <Field
            label="Starts"
            htmlFor={`${id}-starts`}
            required={v.enabled}
            error={errors.starts_at?.[0]}
            hint="Your time zone. It replaces the current banner then."
          >
            <Input
              id={`${id}-starts`}
              type="datetime-local"
              disabled={off}
              value={toLocalInput(v.starts_at ?? '')}
              onChange={(e) =>
                set({ starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' })
              }
            />
          </Field>
        )}
        <Field
          label="Ends (optional)"
          htmlFor={`${id}-ends`}
          error={errors.ends_at?.[0]}
          hint="Your time zone. Empty: shows until you turn it off."
        >
          <Input
            id={`${id}-ends`}
            type="datetime-local"
            disabled={off}
            value={toLocalInput(v.ends_at)}
            onChange={(e) => set({ ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
          />
        </Field>
        <Field label="Show it on" htmlFor={`${id}-pages`} error={errors.pages?.[0]}>
          <NativeSelect
            id={`${id}-pages`}
            disabled={off}
            value={v.pages}
            onChange={(e) => set({ pages: e.target.value as 'home' | 'all' })}
          >
            <option value="home">The homepage only</option>
            <option value="all">Every page of arvaseo.com</option>
          </NativeSelect>
        </Field>
      </div>
      <div className="fld">
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>Preview</span>
        <div className="preview">
          <AnnouncementBar
            tone="info"
            message={v.message}
            code={v.code}
            link={v.link_url ? v.link_label || 'Learn more' : ''}
          />
        </div>
      </div>
    </>
  )
}

function SiteAnnouncementForm({ data }: { data: SettingsResponse }) {
  const f = useForm('site_announcement', data)
  const next = useForm('site_announcement_next', data)
  const off = !data.can_edit

  return (
    <>
      <Panel
        title="Website banner"
        sub={
          'A bar across the top of arvaseo.com for promos and news, with an optional promo code visitors can copy. It hides itself after the end date. Visitors can close it; a new message shows again.'
        }
      >
        <form onSubmit={f.submit} className="form">
          <SwitchRow
            label="Show the banner on arvaseo.com"
            sub="It hides itself after the end date"
            checked={f.value.enabled}
            disabled={off}
            onChange={(on) => f.set({ enabled: on })}
          />
          <BannerFields id="s" v={f.value} set={f.set} errors={f.errors} off={off} />
          <Foot data={data} k="site_announcement" saving={f.saving} />
        </form>
      </Panel>
      <Panel
        title="Next banner"
        sub="Set up ahead, for a launch or a sale: from its start it replaces the banner above, without anyone switching it."
      >
        <form onSubmit={next.submit} className="form">
          <SwitchRow
            label="Show it from its start"
            sub="Off: it waits here and never shows"
            checked={next.value.enabled}
            disabled={off}
            onChange={(on) => next.set({ enabled: on })}
          />
          <BannerFields id="n" v={next.value} set={next.set} errors={next.errors} off={off} />
          <Foot data={data} k="site_announcement_next" saving={next.saving} />
        </form>
      </Panel>
    </>
  )
}

function PaidPlansForm({ data }: { data: SettingsResponse }) {
  const f = useForm('billing', data)
  const off = !data.can_edit

  return (
    <Panel
      title="Paid plans"
      sub={
        'Turn purchases off while payments can’t be taken (for example, before the payment provider approves the store, or during maintenance). Customers see the message below, if there is one, instead of the buy buttons; they can still cancel or move to a smaller plan, and you can still change plans for them here.'
      }
    >
      <form onSubmit={f.submit} className="form">
        <SwitchRow
          label="Customers can buy plans, upgrades and add-ons"
          sub="Turn off while payments can’t be taken"
          checked={f.value.upgrades_open}
          disabled={off}
          onChange={(on) => f.set({ upgrades_open: on })}
        />
        <Field
          label="Message while paused"
          htmlFor="b-message"
          error={f.errors.paused_message?.[0]}
          hint="Shown on Plan & billing and on the website’s pricing page. Leave it empty to say nothing: the buy buttons are simply hidden and the website’s plans all say Start free."
        >
          <Textarea
            id="b-message"
            rows={2}
            maxLength={280}
            disabled={off}
            value={f.value.paused_message}
            onChange={(e) => f.set({ paused_message: e.target.value })}
          />
        </Field>
        <Foot data={data} k="billing" saving={f.saving} />
      </form>
    </Panel>
  )
}

function SupportForm({ data }: { data: SettingsResponse }) {
  const f = useForm('support', data)
  const off = !data.can_edit

  return (
    <Panel
      title="Support promise"
      sub={"On the app's Support page, the website enquiry page, and the “we got your message” email."}
    >
      <form onSubmit={f.submit} className="form">
        <Field label="When we reply" htmlFor="s-time" required error={f.errors.response_time?.[0]}>
          <Input
            id="s-time"
            disabled={off}
            value={f.value.response_time}
            onChange={(e) => f.set({ response_time: e.target.value })}
          />
        </Field>
        <Field label="Support hours" htmlFor="s-hours" error={f.errors.hours?.[0]}>
          <Input
            id="s-hours"
            disabled={off}
            value={f.value.hours}
            onChange={(e) => f.set({ hours: e.target.value })}
          />
        </Field>
        <Foot data={data} k="support" saving={f.saving} />
      </form>
    </Panel>
  )
}

const linkLabels: Record<keyof AppSettings['links'], string> = {
  website_url: 'Website',
  blog_url: 'Blog',
  help_url: 'Help center',
  status_url: 'Status page',
  terms_url: 'Terms of Service',
  privacy_url: 'Privacy Policy',
  refund_url: 'Refund policy',
  review_url: 'Google review page',
}

function LinksForm({ data }: { data: SettingsResponse }) {
  const f = useForm('links', data)
  const off = !data.can_edit

  return (
    <Panel
      title="Links"
      sub={
        'Where the app points people. The legal pages show next to the plans. Leave a link empty to hide it.'
      }
    >
      <form onSubmit={f.submit} className="form">
        {(Object.keys(linkLabels) as (keyof AppSettings['links'])[]).map((k) => (
          <Field key={k} label={linkLabels[k]} htmlFor={`l-${k}`} error={f.errors[k]?.[0]}>
            <Input
              id={`l-${k}`}
              type="url"
              disabled={off}
              value={f.value[k]}
              onChange={(e) => f.set({ [k]: e.target.value })}
              placeholder="https://"
            />
          </Field>
        ))}
        <Foot data={data} k="links" saving={f.saving} />
      </form>
    </Panel>
  )
}

function ReferralsForm({ data }: { data: SettingsResponse }) {
  const f = useForm('referrals', data)
  const discounts = useDiscounts()
  const off = !data.can_edit
  // Codes for everyone that work now; a personal offer or a removed code can't be the referral discount.
  const choices = (discounts.data?.data ?? []).filter((d) => d.status === 'active' && d.customer === null)
  const missing = f.value.discount_code !== '' && !choices.some((d) => d.code === f.value.discount_code)

  return (
    <Panel
      title="Referral program"
      sub={
        'Workspace owners refer new accounts with their link (on the app’s Referrals page) and earn a commission on each account’s first plan payment, held 14 days. See how it’s doing under Referrals.'
      }
    >
      <form onSubmit={f.submit} className="form">
        <SwitchRow
          label="Run the referral program"
          sub="Owners see Referrals, and referred accounts earn commissions"
          checked={f.value.enabled}
          disabled={off}
          onChange={(on) => f.set({ enabled: on })}
        />
        <Field
          label="Commission (%)"
          htmlFor="r-commission"
          required
          error={f.errors.commission_percent?.[0]}
          hint="Of a referred account’s first plan payment, before tax. Changes apply to new commissions only."
        >
          <Input
            id="r-commission"
            type="number"
            min={0}
            max={100}
            disabled={off}
            value={f.value.commission_percent}
            onChange={(e) => f.set({ commission_percent: Number(e.target.value) })}
            className="w-28"
          />
        </Field>
        <Field
          label="Discount for referred accounts"
          htmlFor="r-discount"
          error={f.errors.discount_code?.[0]}
          hint="Filled in at their first plan checkout. Make the code under Discounts first (for example 10% off, first payment). Referrals are only known when the visitor allows partner referral cookies."
        >
          <NativeSelect
            id="r-discount"
            disabled={off || discounts.isPending}
            value={f.value.discount_code}
            onChange={(e) => f.set({ discount_code: e.target.value })}
          >
            <option value="">No discount</option>
            {missing && (
              <option value={f.value.discount_code}>{f.value.discount_code} (no longer active)</option>
            )}
            {choices.map((d) => (
              <option key={d.id} value={d.code}>
                {d.code}: {d.summary}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field
          label="Smallest PayPal payout ($)"
          htmlFor="r-minimum"
          required
          error={f.errors.minimum_payout?.[0]}
          hint="Partners can ask for their balance by PayPal once it reaches this."
        >
          <Input
            id="r-minimum"
            type="number"
            min={0}
            step={1}
            disabled={off}
            value={f.value.minimum_payout / 100}
            onChange={(e) => f.set({ minimum_payout: Math.round(Number(e.target.value) * 100) })}
            className="w-28"
          />
        </Field>
        <Foot data={data} k="referrals" saving={f.saving} />
      </form>
    </Panel>
  )
}

function WordPressForm({ data }: { data: SettingsResponse }) {
  const f = useForm('wordpress', data)
  const off = !data.can_edit
  const toggles = [
    ['connector_enabled', 'WordPress connector on (pairing, uploads and deliveries)'],
    ['health_enabled', 'Accept and assess Website Health snapshots'],
    ['publishing_enabled', 'Allow sending content to WordPress'],
  ] as const

  return (
    <Panel
      title="WordPress connector"
      sub={
        "Switches for the Arva SEO Connector plugin, checked on the server for every request. While the connector is off, the app also hides its WordPress pages, Website Health and Send to WordPress. Turning one off doesn't disconnect sites; they resume when it's back on. Not tied to a plan yet."
      }
    >
      <form onSubmit={f.submit} className="form">
        {toggles.map(([key, label]) => (
          <SwitchRow
            key={key}
            label={label}
            checked={f.value[key]}
            disabled={off}
            onChange={(on) => f.set({ [key]: on })}
          />
        ))}
        <Foot data={data} k="wordpress" saving={f.saving} />
      </form>
    </Panel>
  )
}

/**
 * Cloudflare's copy of arvaseo.com and its subdomains: cleared after each website deploy by
 * itself; here, by hand after a change that has to show at once (owners and admins). Everything,
 * or up to 30 addresses. Each clear is in the audit log.
 */
function CloudflareCacheForm() {
  const status = useCloudflareCache()
  const clear = useClearCloudflareCache()
  const [confirming, setConfirming] = useState(false)
  const [urls, setUrls] = useState('')

  if (status.isPending) return <Skeleton className="h-48 rounded-[14px]" />
  if (status.isError) {
    return (
      <div className="card">
        <ErrorState error={status.error} onRetry={() => void status.refetch()} />
      </div>
    )
  }
  const s = status.data
  const off = !s.configured || !s.can_clear || clear.isPending
  const list = urls
    .split(/\s+/)
    .map((u) => u.trim())
    .filter(Boolean)
  const run = (input: { everything: true } | { everything: false; urls: string[] }) =>
    clear.mutate(input, {
      onSuccess: () => {
        setConfirming(false)
        if (!input.everything) setUrls('')
        toast.success(
          input.everything
            ? 'Cloudflare’s cache is cleared. Pages come from the server until Cloudflare copies them again.'
            : `Cleared ${input.urls.length} ${input.urls.length === 1 ? 'address' : 'addresses'} on Cloudflare.`,
        )
      },
      onError: (err) => toast.error(errorMessage(err)),
    })

  return (
    <Panel
      title="Cloudflare cache"
      sub="Cloudflare keeps a copy of the website’s pages and files near each visitor. Each website deploy clears it by itself; clear it here when a change has to show at once."
    >
      <div className="form">
        {!s.configured && (
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>
            Cloudflare isn’t connected yet. Add CLOUDFLARE_API_TOKEN (a token that can only purge
            arvaseo.com’s cache) and CLOUDFLARE_ZONE_ID to the server’s settings, then this works.
          </p>
        )}
        <div className="srow" style={{ paddingTop: 0 }}>
          <div>
            <b>Clear everything</b>
            <small>Every page and file on arvaseo.com and its subdomains.</small>
          </div>
          {confirming ? (
            <span className="bar">
              <button type="button" className="btn sm" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn sm primary"
                disabled={off}
                onClick={() => run({ everything: true })}
              >
                {clear.isPending ? 'Clearing…' : 'Yes, clear everything'}
              </button>
            </span>
          ) : (
            <button type="button" className="btn sm" disabled={off} onClick={() => setConfirming(true)}>
              Clear everything
            </button>
          )}
        </div>
        <Field label={`Or only these addresses (one per line, up to ${s.max_urls})`} htmlFor="cf-urls">
          <Textarea
            id="cf-urls"
            rows={3}
            value={urls}
            disabled={!s.configured || !s.can_clear}
            onChange={(e) => setUrls(e.target.value)}
            placeholder="https://arvaseo.com/pricing/"
          />
        </Field>
        <div className="bar">
          <span style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
            {s.last
              ? `Last cleared ${formatRelative(s.last.at)}${s.last.by ? ` by ${s.last.by}` : ''}: ${
                  s.last.everything
                    ? 'everything'
                    : `${s.last.urls} ${s.last.urls === 1 ? 'address' : 'addresses'}`
                }`
              : 'Not cleared from here yet'}
          </span>
          <button
            type="button"
            className="btn primary push"
            disabled={off || list.length === 0 || list.length > s.max_urls}
            onClick={() => run({ everything: false, urls: list })}
          >
            {clear.isPending ? 'Clearing…' : 'Clear these'}
          </button>
        </div>
      </div>
    </Panel>
  )
}

function RankingUpdatesForm({ data }: { data: SettingsResponse }) {
  const defaults: NonNullable<AppSettings['ranking_updates']> = {
    checked_on: null,
    updates: [],
    expectations: [
      'Indexing: days, once crawled.',
      'Impressions and position: often 2–6 weeks.',
      'Clicks: often 4–8 weeks.',
      'Enquiries: often 8–12 weeks. Typical ranges, never promises.',
    ],
  }
  const f = useForm('ranking_updates', data, defaults)
  const v = f.value ?? defaults
  const off = !data.can_edit
  return (
    <Panel
      title="Google ranking updates"
      sub="Record confirmed updates and when you checked the list. Empty does not mean there was no update. These appear as light chart bands, separate from customers’ work."
    >
      <form onSubmit={f.submit} className="form">
        <Field label="Last checked" htmlFor="updates-checked" error={f.errors.checked_on?.[0]}>
          <Input
            id="updates-checked"
            type="date"
            value={v.checked_on ?? ''}
            disabled={off}
            onChange={(e) => f.set({ checked_on: e.target.value || null })}
          />
        </Field>
        {v.updates.map((u, i) => (
          <div key={i} className="grid gap-3">
            {(['title', 'start', 'end', 'source'] as const).map((k) => (
              <Field
                key={k}
                label={
                  {
                    title: 'Update name',
                    start: 'Start date',
                    end: 'End date',
                    source: 'Official source URL',
                  }[k]
                }
                htmlFor={`update-${i}-${k}`}
                required
                error={f.errors[`updates.${i}.${k}`]?.[0]}
              >
                <Input
                  id={`update-${i}-${k}`}
                  type={k === 'start' || k === 'end' ? 'date' : k === 'source' ? 'url' : 'text'}
                  value={u[k]}
                  disabled={off}
                  onChange={(e) =>
                    f.set({ updates: v.updates.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)) })
                  }
                />
              </Field>
            ))}
            <button
              type="button"
              className="btn"
              disabled={off}
              onClick={() => f.set({ updates: v.updates.filter((_, j) => j !== i) })}
            >
              Remove this update
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn"
          disabled={off || v.updates.length >= 100}
          onClick={() => f.set({ updates: [...v.updates, { title: '', start: '', end: '', source: '' }] })}
        >
          Add update
        </button>
        <Field
          label="Typical timing: one line each, never promises"
          htmlFor="measurement-timing"
          required
          error={f.errors.expectations?.[0] ?? f.errors['expectations.0']?.[0]}
        >
          <Textarea
            id="measurement-timing"
            value={v.expectations.join('\n')}
            disabled={off}
            onChange={(e) => f.set({ expectations: e.target.value.split('\n') })}
          />
        </Field>
        <Foot data={data} k="ranking_updates" saving={f.saving} />
      </form>
    </Panel>
  )
}
